import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { Button } from '@/components/button/Button'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { Modal, ModalContainer, ModalFooterWithActions, ModalFooterWithActionsType, ModalTitle } from '@/components/modal'
import { ModalDescription } from '@/components/modal/Modal'
import { kExternalStorageProviderDisplayName } from '@/constants/constants-external-storage'
import type { ExternalStorageDisconnectFileDisposition } from '@/settings/schemas/external-storage-schema'
import { getMatterExternalSyncMappingDisconnectPreview } from '@/user_files/external-storage/fetch/get-matter-external-sync-mapping-disconnect-preview'
import type { MatterExternalSyncMappingPublicRead } from '@/user_files/external-storage/schemas'

interface DisconnectMatterFolderDialogProps {
  open: boolean
  matterId: string
  folderName: string
  provider: MatterExternalSyncMappingPublicRead['provider']
  loading: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (fileDisposition: ExternalStorageDisconnectFileDisposition) => void
}

interface DispositionCardProps {
  disposition: ExternalStorageDisconnectFileDisposition
  label: string
  description: string
  selected: boolean
  disabled: boolean
  recommended?: boolean
  onSelect: (disposition: ExternalStorageDisconnectFileDisposition) => void
}

function DispositionCard({ disposition, label, description, selected, disabled, recommended = false, onSelect }: DispositionCardProps) {
  const selectedStyles = disposition === 'keep' ? 'border-brand-200 bg-brand-50' : 'border-red-300 bg-red-50'
  const selectedRadioStyles = disposition === 'keep' ? 'border-brand-500' : 'border-brand-neutral-950'

  return (
    <label
      className={clsx(
        'flex flex-col gap-2 rounded-lg border p-4',
        selected ? selectedStyles : 'border-brand-neutral-200 bg-brand-neutral-50',
        disabled ? 'cursor-default' : 'cursor-pointer'
      )}
    >
      <span className="flex items-start gap-2">
        <span className="flex min-h-6 min-w-0 flex-1 items-start gap-2">
          <input
            className="peer sr-only"
            type="radio"
            name="matter-external-storage-file-disposition"
            value={disposition}
            aria-label={label}
            checked={selected}
            disabled={disabled}
            onChange={() => onSelect(disposition)}
          />
          <span
            aria-hidden
            className={clsx(
              'mt-0.5 size-5 shrink-0 rounded-full bg-white peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2',
              selected ? `border-[5px] ${selectedRadioStyles}` : 'border border-brand-neutral-300'
            )}
          />
          <span className="font-rethink-sans text-base font-medium leading-6 text-brand-neutral-950">{label}</span>
        </span>
        {recommended && <span className="shrink-0 rounded bg-brand-neutral-950 px-1 py-px text-[10px] font-medium leading-[11px] text-white">RECOMMENDED</span>}
      </span>
      <span className="pl-7 text-sm leading-5 tracking-[-0.15px] text-brand-neutral-800">{description}</span>
    </label>
  )
}

export function DisconnectMatterFolderDialog({ open, matterId, folderName, provider, loading, onOpenChange, onConfirm }: DisconnectMatterFolderDialogProps) {
  const providerLabel = kExternalStorageProviderDisplayName[provider]
  const [disposition, setDisposition] = useState<ExternalStorageDisconnectFileDisposition>('keep')
  const [fileCount, setFileCount] = useState<number | null>(null)
  const [previewError, setPreviewError] = useState(false)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)

  useEffect(() => {
    if (open) setDisposition('keep')
  }, [matterId, open])

  useEffect(() => {
    if (!open) return

    let active = true
    setFileCount(null)
    setPreviewError(false)
    setIsLoadingPreview(true)

    void getMatterExternalSyncMappingDisconnectPreview(matterId)
      .then((preview) => {
        if (active) setFileCount(preview.file_count)
      })
      .catch(() => {
        if (active) setPreviewError(true)
      })
      .finally(() => {
        if (active) setIsLoadingPreview(false)
      })

    return () => {
      active = false
    }
  }, [matterId, loadAttempt, open])

  const countLabel = fileCount === null ? null : `${fileCount.toLocaleString()} ${fileCount === 1 ? 'file' : 'files'}`

  return (
    <Modal open={open} onOpenChange={(nextOpen) => !loading && onOpenChange(nextOpen)}>
      <ModalContainer width="md" height="max-md" className="max-w-[568px]" preventDismiss={loading}>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col p-6">
          <div className="shrink-0 pb-4">
            <ModalTitle className="tracking-normal">Disconnect synced folder?</ModalTitle>
            <ModalDescription className="mt-1.5 font-sans text-base font-normal leading-6 tracking-[-0.32px] text-brand-neutral-950">
              This will disconnect {providerLabel} folder <strong className="font-semibold">{folderName}</strong> from this matter.
              {countLabel && ` Choose what happens to the ${countLabel} already in this matter.`}
            </ModalDescription>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <fieldset className="flex flex-col gap-4" disabled={loading}>
              <legend className="sr-only">Choose what happens to files already in this matter</legend>
              <DispositionCard
                disposition="keep"
                label="Keep files in this matter"
                description={`Files stay in this matter and remain searchable. They stop updating from ${providerLabel}.`}
                selected={disposition === 'keep'}
                disabled={loading}
                recommended
                onSelect={setDisposition}
              />
              {isLoadingPreview ? (
                <div className="flex items-center justify-center gap-3 py-4 text-sm text-brand-neutral-700" aria-busy="true">
                  <CircularProgressContinuousSized size={20} thickness={6} />
                  Loading synced file counts…
                </div>
              ) : previewError ? (
                <InlineMessage variant="error" className="w-full items-center">
                  <span className="flex flex-wrap items-center justify-between gap-2">
                    Unable to load synced file counts.
                    <Button color="destructive" size="xs" variant="plain" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
                      Retry
                    </Button>
                  </span>
                </InlineMessage>
              ) : countLabel ? (
                <DispositionCard
                  disposition="remove"
                  label="Remove files from this matter"
                  description={`${countLabel} ${
                    fileCount === 1 ? 'is' : 'are'
                  } removed from this matter. Any references to removed files will be broken. This can't be undone.`}
                  selected={disposition === 'remove'}
                  disabled={loading}
                  onSelect={setDisposition}
                />
              ) : null}
            </fieldset>
          </div>

          <div className="mt-auto shrink-0 pt-6">
            <ModalFooterWithActions
              type={ModalFooterWithActionsType.DESTRUCTIVE}
              secondaryAction={{ label: 'Cancel', onClick: () => onOpenChange(false), disabled: loading }}
              primaryAction={{
                label: 'Disconnect',
                onClick: () => onConfirm(disposition),
                loading,
                disabled: loading || (disposition === 'remove' && fileCount === null),
              }}
            />
          </div>
        </div>
      </ModalContainer>
    </Modal>
  )
}
